import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web
import { persistReducer, persistStore } from 'redux-persist';

// SLICES
import authReducer from './slices/authSlice';
import fetchReducer from './slices/fetchSlice';
import localStorageReducer, {
  initCacheStates,
} from './slices/localStorageSlice';
import coreReducer from './slices/coreSlice';
import pollDraftReducer from './slices/pollDraftSlice';
import searchModalSlice from './slices/searchModalSlice';
import searchFilterSlice from './slices/searchFiltersSlice';

// SERVICES
import { logsApi } from './services/logs';
import { menuApi } from './services/menu';
import { pollsApi } from './services/polls';
import { departmentsApi } from './services/departments';
import { calendarApi } from './services/events.jsx';
import { usersApi } from './services/users';
import { rolesApi } from './services/roles';
import { ldapApi } from './services/ldap';
import { permissionsApi } from './services/permissions';
import { usefulLinksApi } from './services/useful_links.jsx';
import { authApi } from './services/authApi';
import { newsApi } from './services/news';
import { articlesApi } from './services/articles.jsx';
import { slidersApi } from './services/sliders';
import { blocksApi } from './services/blocks';
import { internalJobsApi } from './services/fij_internal_jobs';
import { areaOfUsesApi } from './services/fij_area_of_uses';
import { studyProgramsApi } from './services/study_programs';
import { interestsApi } from './services/fij_applications';
import { wikiApi } from './services/wikiApi.jsx';
import { filesApi } from './services/filesApi';
import { dissertationsApi } from './services/dissertations';
import { mailApi } from './services/mail';
import { roomsApi } from './services/rooms';
import { roomsResponsiblePersonsApi } from './services/rooms_responsible';
import { reservationsApi } from './services/reservations';
import { ingProjectsApi } from './services/ing_projects';
import { ingProjectsSettingsApi } from './services/ing_projects_settings';
import { searchApi } from './services/search';
import { searchHistoryApi } from './services/search_history.jsx';
import { fjJobsApi } from './services/fj_jobs';
import { fjJobsTypesApi } from './services/fj_jobs_types';
import { fjInterestsApi } from './services/fj_interests';
import { fjClickStatsApi } from './services/fj_click_stats';
import { externalPartnersApi } from './services/externalPartners.jsx';
import { externalUsersApi } from './services/externalUsers';
import { eduCoursesApi } from './services/eduCourses';
import { eduCourseDatesApi } from './services/eduCourseDates';
import { attributeUnitsApi } from './services/attributeUnits';
import { sensorsApi } from './services/sensors';
import { sensorAttributesApi } from './services/sensorAttributes';
import { sensorValuesApi } from './services/sensorValues';
import { passwordResetApi } from './services/passwordReset.jsx';
import { yellowBooksApi } from './services/yellow_books';
import { erasmusApplicationsApi } from './services/erasmusApplications';
import { erasmusUniversitiesApi } from './services/erasmusUniversities';
import { erasmusMobilitiesApi } from './services/erasmusMobilities';
import { erasmusMobilityTypesApi } from './services/erasmusMobilityTypes';
import { erasmusMobilitySubtypesApi } from './services/erasmusMobilitySubtypes';
import { erasmusConsortiumApi } from './services/erasmusConsortium';
import { erasmusChallengesApi } from './services/erasmusChallenges';
import { erasmusSubjectApi } from './services/erasmusSubjects';
import { erasmusQuestionnaireApi } from './services/erasmusQuestionnaire';
import { phdApi } from './services/phd';
import autoMergeLevel2 from 'redux-persist/es/stateReconciler/autoMergeLevel2';
import createMigrate from 'redux-persist/es/createMigrate';
import migrations from './migrations';
import { studySubjectsWsApi } from './services/study_subjects_ws';
import { studySubjectsApi } from './services/study_subjects';
import { studyProgramTypesApi } from './services/study_program_types.jsx';
import { eventsApi } from './services/fo_events';
import { workshopsApi } from './services/fo_workshops.jsx';
import { datesApi } from './services/fo_dates.jsx';
import { attendanceApi } from './services/attendance';
import logoutListenerMiddleware from './middlewares/logoutListenerMiddleware.jsx';
import { quizzesApi } from './services/quizzes.jsx';
import { quizQuestionsApi } from './services/quiz_questions.jsx';
import { quizStudentAnswersApi } from './services/quiz_student_answers.jsx';
import { quizRunsApi } from './services/quiz_runs.jsx';
import { evaluationFunctionsApi } from './services/evaluationFunctions.jsx';
import { translationApi } from './services/translations.jsx';
import { ingProjectAssignmentsApi } from './services/ing_projects_assignments.jsx';
import { ingProjectsChangeRequestsApi } from './services/ing_projects_change_requests.jsx';
import { ingProjectsPanelStoryApi } from './services/ing_projects_panel_story';
import { helpApi } from './services/help.jsx';
import { appVersionApi } from './services/appVersion.jsx';
import { facultyStatisticsApi } from './services/faculty_statistics.jsx';
import { bugReportsApi } from './services/bug_reports.jsx';
import { clientErrorsApi } from './services/client_errors.jsx';

const MIGRATION_VERSION = 0;

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth', 'localStorage'], // only this reducer will be persisted
  version: MIGRATION_VERSION,
  stateReconciler: autoMergeLevel2,
  migrate: createMigrate(migrations),
};

const persistedReducer = persistReducer(
  persistConfig,
  combineReducers({
    core: coreReducer,
    auth: authReducer,
    fetch: fetchReducer,
    localStorage: localStorageReducer,
    pollDraft: pollDraftReducer,
    searchModal: searchModalSlice,
    searchFilter: searchFilterSlice,
    [authApi.reducerPath]: authApi.reducer,
    [studySubjectsWsApi.reducerPath]: studySubjectsWsApi.reducer,
    [studySubjectsApi.reducerPath]: studySubjectsApi.reducer,
    [pollsApi.reducerPath]: pollsApi.reducer,
    [departmentsApi.reducerPath]: departmentsApi.reducer,
    [calendarApi.reducerPath]: calendarApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [rolesApi.reducerPath]: rolesApi.reducer,
    [ldapApi.reducerPath]: ldapApi.reducer,
    [permissionsApi.reducerPath]: permissionsApi.reducer,
    [usefulLinksApi.reducerPath]: usefulLinksApi.reducer,
    [newsApi.reducerPath]: newsApi.reducer,
    [articlesApi.reducerPath]: articlesApi.reducer,
    [menuApi.reducerPath]: menuApi.reducer,
    [slidersApi.reducerPath]: slidersApi.reducer,
    [logsApi.reducerPath]: logsApi.reducer,
    [blocksApi.reducerPath]: blocksApi.reducer,
    [internalJobsApi.reducerPath]: internalJobsApi.reducer,
    [areaOfUsesApi.reducerPath]: areaOfUsesApi.reducer,
    [studyProgramsApi.reducerPath]: studyProgramsApi.reducer,
    [studyProgramTypesApi.reducerPath]: studyProgramTypesApi.reducer,
    [interestsApi.reducerPath]: interestsApi.reducer,
    [wikiApi.reducerPath]: wikiApi.reducer,
    [filesApi.reducerPath]: filesApi.reducer,
    [dissertationsApi.reducerPath]: dissertationsApi.reducer,
    [mailApi.reducerPath]: mailApi.reducer,
    [roomsApi.reducerPath]: roomsApi.reducer,
    [roomsResponsiblePersonsApi.reducerPath]:
      roomsResponsiblePersonsApi.reducer,
    [reservationsApi.reducerPath]: reservationsApi.reducer,
    [ingProjectsApi.reducerPath]: ingProjectsApi.reducer,
    [ingProjectsSettingsApi.reducerPath]: ingProjectsSettingsApi.reducer,
    [searchApi.reducerPath]: searchApi.reducer,
    [searchHistoryApi.reducerPath]: searchHistoryApi.reducer,
    [fjJobsApi.reducerPath]: fjJobsApi.reducer,
    [fjJobsTypesApi.reducerPath]: fjJobsTypesApi.reducer,
    [fjInterestsApi.reducerPath]: fjInterestsApi.reducer,
    [fjClickStatsApi.reducerPath]: fjClickStatsApi.reducer,
    [externalPartnersApi.reducerPath]: externalPartnersApi.reducer,
    [externalUsersApi.reducerPath]: externalUsersApi.reducer,
    [eduCoursesApi.reducerPath]: eduCoursesApi.reducer,
    [eduCourseDatesApi.reducerPath]: eduCourseDatesApi.reducer,
    [sensorsApi.reducerPath]: sensorsApi.reducer,
    [sensorAttributesApi.reducerPath]: sensorAttributesApi.reducer,
    [attributeUnitsApi.reducerPath]: attributeUnitsApi.reducer,
    [sensorValuesApi.reducerPath]: sensorValuesApi.reducer,
    [passwordResetApi.reducerPath]: passwordResetApi.reducer,
    [eventsApi.reducerPath]: eventsApi.reducer,
    [workshopsApi.reducerPath]: workshopsApi.reducer,
    [datesApi.reducerPath]: datesApi.reducer,
    [yellowBooksApi.reducerPath]: yellowBooksApi.reducer,
    [erasmusApplicationsApi.reducerPath]: erasmusApplicationsApi.reducer,
    [erasmusUniversitiesApi.reducerPath]: erasmusUniversitiesApi.reducer,
    [erasmusMobilitiesApi.reducerPath]: erasmusMobilitiesApi.reducer,
    [erasmusMobilityTypesApi.reducerPath]: erasmusMobilityTypesApi.reducer,
    [erasmusMobilitySubtypesApi.reducerPath]:
      erasmusMobilitySubtypesApi.reducer,
    [erasmusConsortiumApi.reducerPath]: erasmusConsortiumApi.reducer,
    [erasmusChallengesApi.reducerPath]: erasmusChallengesApi.reducer,
    [erasmusSubjectApi.reducerPath]: erasmusSubjectApi.reducer,
    [erasmusQuestionnaireApi.reducerPath]: erasmusQuestionnaireApi.reducer,
    [attendanceApi.reducerPath]: attendanceApi.reducer,
    [quizzesApi.reducerPath]: quizzesApi.reducer,
    [quizQuestionsApi.reducerPath]: quizQuestionsApi.reducer,
    [quizStudentAnswersApi.reducerPath]: quizStudentAnswersApi.reducer,
    [quizRunsApi.reducerPath]: quizRunsApi.reducer,
    [evaluationFunctionsApi.reducerPath]: evaluationFunctionsApi.reducer,
    [phdApi.reducerPath]: phdApi.reducer,
    [translationApi.reducerPath]: translationApi.reducer,
    [ingProjectAssignmentsApi.reducerPath]: ingProjectAssignmentsApi.reducer,
    [ingProjectsChangeRequestsApi.reducerPath]:
      ingProjectsChangeRequestsApi.reducer,
    [ingProjectsPanelStoryApi.reducerPath]: ingProjectsPanelStoryApi.reducer,
    [helpApi.reducerPath] : helpApi.reducer,
    [appVersionApi.reducerPath]: appVersionApi.reducer,
    [facultyStatisticsApi.reducerPath]: facultyStatisticsApi.reducer,
    [bugReportsApi.reducerPath]: bugReportsApi.reducer,
    [clientErrorsApi.reducerPath]: clientErrorsApi.reducer,

  }),
);

export const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV !== 'production',
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    })
      .concat(logoutListenerMiddleware)
      .concat(wikiApi.middleware)
      .concat(appVersionApi.middleware)
      .concat(facultyStatisticsApi.middleware)
      .concat(bugReportsApi.middleware)
      .concat(clientErrorsApi.middleware)
      .concat(logsApi.middleware)
      .concat(menuApi.middleware)
      .concat(authApi.middleware)
      .concat(pollsApi.middleware)
      .concat(departmentsApi.middleware)
      .concat(calendarApi.middleware)
      .concat(usersApi.middleware)
      .concat(rolesApi.middleware)
      .concat(ldapApi.middleware)
      .concat(permissionsApi.middleware)
      .concat(usefulLinksApi.middleware)
      .concat(newsApi.middleware)
      .concat(articlesApi.middleware)
      .concat(studyProgramTypesApi.middleware)
      .concat(studySubjectsWsApi.middleware)
      .concat(studySubjectsApi.middleware)
      .concat(blocksApi.middleware)
      .concat(slidersApi.middleware)
      .concat(internalJobsApi.middleware)
      .concat(areaOfUsesApi.middleware)
      .concat(studyProgramsApi.middleware)
      .concat(interestsApi.middleware)
      .concat(filesApi.middleware)
      .concat(dissertationsApi.middleware)
      .concat(mailApi.middleware)
      .concat(roomsApi.middleware)
      .concat(roomsResponsiblePersonsApi.middleware)
      .concat(reservationsApi.middleware)
      .concat(ingProjectsApi.middleware)
      .concat(ingProjectsSettingsApi.middleware)
      .concat(searchApi.middleware)
      .concat(searchHistoryApi.middleware)
      .concat(fjJobsApi.middleware)
      .concat(fjJobsTypesApi.middleware)
      .concat(fjInterestsApi.middleware)
      .concat(fjClickStatsApi.middleware)
      .concat(externalPartnersApi.middleware)
      .concat(externalUsersApi.middleware)
      .concat(eduCoursesApi.middleware)
      .concat(eduCourseDatesApi.middleware)
      .concat(sensorsApi.middleware)
      .concat(sensorAttributesApi.middleware)
      .concat(attributeUnitsApi.middleware)
      .concat(sensorValuesApi.middleware)
      .concat(passwordResetApi.middleware)
      .concat(eventsApi.middleware)
      .concat(workshopsApi.middleware)
      .concat(datesApi.middleware)
      .concat(yellowBooksApi.middleware)
      .concat(erasmusApplicationsApi.middleware)
      .concat(erasmusUniversitiesApi.middleware)
      .concat(erasmusMobilitiesApi.middleware)
      .concat(erasmusMobilityTypesApi.middleware)
      .concat(erasmusMobilitySubtypesApi.middleware)
      .concat(erasmusConsortiumApi.middleware)
      .concat(erasmusChallengesApi.middleware)
      .concat(erasmusSubjectApi.middleware)
      .concat(erasmusQuestionnaireApi.middleware)
      .concat(quizzesApi.middleware)
      .concat(quizQuestionsApi.middleware)
      .concat(quizStudentAnswersApi.middleware)
      .concat(quizRunsApi.middleware)
      .concat(evaluationFunctionsApi.middleware)
      .concat(phdApi.middleware)
      .concat(translationApi.middleware)
      .concat(ingProjectAssignmentsApi.middleware)
      .concat(ingProjectsChangeRequestsApi.middleware)
      .concat(ingProjectsPanelStoryApi.middleware)
      .concat(helpApi.middleware),

});

setupListeners(store.dispatch);

export const persistor = persistStore(store, null, () => {
  store.dispatch(initCacheStates());
});
